import { Button, Cell, Grid, Icon, Tooltip } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import CheckPermission from 'components/auth/CheckPermission'
import { useOrientacoesQuery } from 'graphql/hooks.generated'
import React, { useMemo, useState } from 'react'
import { formatDate } from 'util/date/formatDate'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'

import { grupoCboOrientacoes } from '../../acessos'
import { OrientacaoFormModel, OrientacaoRowModel } from '../types/OrientacoesModel'
import { mergeSortOrientacoes } from '../util/mergeOrientacoes'
import { OrientacoesAccordionPanel } from './OrientacoesAccordionPanel'
import { OrientacoesTableRow } from './OrientacoesTableRow'

interface OrientacoesTableProps {
  prontuarioId: ID
  orientacoes: OrientacaoFormModel[]
  atendimentoProfissional: AtendimentoProfissional
  idOrientacaoEmEdicao: ID
  update(index: number, value: OrientacaoFormModel): void
  remove(index: number): void
  onEditClick(cacheId: ID): void
}

export function OrientacoesTable(props: OrientacoesTableProps) {
  const {
    prontuarioId,
    orientacoes,
    atendimentoProfissional,
    idOrientacaoEmEdicao,
    onEditClick,
    update,
    remove,
  } = props

  const { data, loading } = useOrientacoesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      orientacaoQueryInput: {
        prontuarioId,
      },
    },
  })

  const [sort, setSort] = useState<string[]>(['-data'])

  const isEditing = (cacheId: ID): boolean => cacheId !== undefined && cacheId === idOrientacaoEmEdicao

  const todasOrientacoes = useMemo(
    () => mergeSortOrientacoes(data.orientacoes ?? [], orientacoes ?? [], atendimentoProfissional, sort),
    [atendimentoProfissional, data.orientacoes, orientacoes, sort]
  )

  const handleEditClick = (e: Event, cacheId: ID) => {
    e.stopPropagation()
    onEditClick(cacheId)
  }

  const handleRemoveClick = (e: Event, cacheId: ID) => {
    e.stopPropagation()
    remove(cacheId)
  }

  return (
    <AccordionDataTable<OrientacaoRowModel>
      loading={loading}
      rows={todasOrientacoes}
      columns={[
        {
          name: 'data',
          header: 'Data',
          render: (item: OrientacaoRowModel) => formatDate(item.atendimentoProfissional.iniciadoEm),
          size: 2,
          sortable: true,
        },
        {
          name: 'profissional',
          header: 'Profissional',
          render: (item: OrientacaoRowModel) => (
            <>
              {item.atendimentoProfissional.lotacao.profissional.nome}
              <br />
              {item.atendimentoProfissional.lotacao.cbo.nome}
            </>
          ),
          size: 4,
          sortable: true,
        },
        {
          name: 'descricao',
          header: 'Orientação',
          render: (item: OrientacaoRowModel) => {
            const itemFromDatabase: boolean = !!item.id
            return (
              <Grid gap={1} gapVertical={0} alignItems='center' wrap={false}>
                <Cell size={9}>{item.descricao.removeTags().truncate(125)}</Cell>
                <Tooltip text='Imprimir'>
                  <Button
                    size='small'
                    kind='normal'
                    skin='ghost'
                    onClick={() => {
                      /* TODO (eldorado): tarefa furura */
                    }}
                  >
                    <Icon icon='printerOutline' />
                  </Button>
                </Tooltip>
                <CheckPermission permission={grupoCboOrientacoes.adicionar}>
                  <Tooltip
                    text={
                      itemFromDatabase
                        ? 'Não é permitido alterar uma orientação cadastrada em outro atendimento.'
                        : 'Alterar'
                    }
                  >
                    <Button
                      size='small'
                      kind='normal'
                      skin='ghost'
                      onClick={(e) => {
                        handleEditClick(e, item.cacheId)
                      }}
                      disabled={itemFromDatabase}
                    >
                      <Icon icon='penOutline' />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    text={
                      itemFromDatabase
                        ? 'Não é permitido excluir uma orientação cadastrada em outro atendimento.'
                        : 'Excluir'
                    }
                  >
                    <Button
                      size='small'
                      kind='normal'
                      skin='ghost'
                      onClick={(e) => {
                        handleRemoveClick(e, item.cacheId)
                      }}
                      disabled={itemFromDatabase}
                    >
                      <Icon icon='trashOutline' />
                    </Button>
                  </Tooltip>
                </CheckPermission>
              </Grid>
            )
          },
          size: 6,
        },
      ]}
      components={{
        AccordionPanel: OrientacoesAccordionPanel,
        Row: (props) => (
          <OrientacoesTableRow
            isEditing={isEditing(props.row.cacheId)}
            onUpdate={update}
            removeOrientacaoEmEdicao={() => onEditClick(null)}
            {...props}
          />
        ),
      }}
      sort={sort}
      onSortChange={setSort}
      disableRow={(row) => isEditing(row.cacheId)}
    />
  )
}
